import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useCallback, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faSort } from "@fortawesome/free-solid-svg-icons/faSort";
import { faSortDown } from "@fortawesome/free-solid-svg-icons/faSortDown";
import { faSortUp } from "@fortawesome/free-solid-svg-icons/faSortUp";

import AccountUpgradeModal from "components/AccountUpgradeModal";
import Button from "components/Button";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import RequestSubscriptionWidget from "components/RequestSubscriptionWidget";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { CUSTOM_OPTION } from "components/ToolDropdownSelector";
import ToolRelativeValueModal from "components/ToolRelativeValueModal";
import ToolTable, { Cell } from "components/ToolTable";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";

import Descriptions from "./Descriptions";
import Filter, { DEFAULT_FILTERS, TYPES } from "./Filters";
import { BACKGROUND_COLORS, relativeValuePairsUrl } from "./utils";

export default function ToolRelativeValue() {
  const router = useRouter();
  const pairId = router.query.pair as string;

  const { mobile } = useMediaQuery();
  const { authenticated, account } = useAuthentication();

  const navigate = mobile ? router.push : router.replace;

  const { trackActivity } = useActivityTracker();

  const [sortKey, setSortKey] = useState("sk-actionability-score");
  const [sortDirection, setSortDirection] = useState(false);
  const [filters, setFilters] = useState<any>(DEFAULT_FILTERS);

  const { toggleModal } = useSignInSignUp();
  const [showStripeModal, toggleStripeModal] = useToggle();

  const [modalRelativeValue, setModalRelativeValue] = useState<
    any | undefined
  >();

  const { data, isValidating, mutate, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.length === 0) return null;
      const options: any = {
        ...(sortKey ? { sort: `${sortDirection ? "" : "-"}${sortKey}` } : {}),
      };

      if (!isEmpty(filters)) {
        Object.keys(filters).forEach((key) => {
          if (filters[key] && filters[key] !== CUSTOM_OPTION) {
            options[`filter[${key}]`] = filters[key];
          }
        });
      }
      return relativeValuePairsUrl(pageIndex + 1, options);
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
    }
  );

  const renderNumberCell = useCallback(
    (value, _record, _isLast, format?: string) => {
      return (
        <Cell
          className={twMerge(
            BACKGROUND_COLORS.score.findLast(
              (v) => v.minValue <= Math.ceil(value)
            )?.backgroundClass
          )}
        >
          {numeral(value).format(format || "0,0")}
        </Cell>
      );
    },
    []
  );

  const renderZScoreCell = useCallback(
    (value, _record, _isLast, format?: string) => {
      const absValue = Math.abs(value);
      return (
        <Cell
          className={twMerge(
            BACKGROUND_COLORS.zScore.findLast(
              (v) => v.minValue <= Math.round(absValue)
            )?.backgroundClass
          )}
        >
          {numeral(absValue).format(format || "0,0.00")}
        </Cell>
      );
    },
    []
  );

  const renderCell = useCallback((value, _record, _isHref) => {
    return <Cell>{value}</Cell>;
  }, []);

  const renderEntityCell = useCallback((value, entity) => {
    return (
      <Cell>
        {entity ? `${entity.prettyName} (${entity.shortSecurity})` : value}
      </Cell>
    );
  }, []);

  const renderHeader = useCallback(
    (value, key) => {
      return (
        <div className={twMerge("flex select-none")}>
          {value}
          <FontAwesomeIcon
            icon={
              sortKey !== key ? faSort : sortDirection ? faSortUp : faSortDown
            }
          />
        </div>
      );
    },
    [sortKey, sortDirection]
  );

  const onSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDirection(!sortDirection);
        return;
      }
      setSortKey(key);
      setSortDirection(true);
    },
    [sortDirection, sortKey]
  );

  const onRowClick = useCallback(
    (record: any) => {
      setModalRelativeValue(record);
      navigate(
        {
          pathname: router.pathname,
          query: { ...(router.query || {}), pair: record.pairId },
        },
        undefined,
        {
          shallow: true,
        }
      );
      trackActivity("relative_value.view_item", undefined, {
        id: record.pairId,
        type: "relative-value-pair-histories",
      });
    },
    [navigate, router.pathname, router.query, trackActivity]
  );

  const tableColumns: any = [
    {
      key: "entityXPrettyName",
      title: "Primary (Numerator)",
      dataIndex: "entityX.prettyName",
      renderHeader: (value: any) => renderHeader(value, `entity-x.pretty-name`),
      render: (value: string, record: any) =>
        renderEntityCell(value, record.entityX),
      onClick: () => {
        onSort(`entity-x.pretty-name`);
      },
    },
    {
      key: "entityYPrettyName",
      title: "Counterpart (Denominator)",
      dataIndex: "entityY.prettyName",
      renderHeader: (value: any) => renderHeader(value, `entity-y.pretty-name`),
      render: (value: string, record: any) =>
        renderEntityCell(value, record.entityY),
      onClick: () => {
        onSort(`entity-y.pretty-name`);
      },
    },
    {
      key: "pairType",
      title: "Type",
      dataIndex: "pairType",
      style: {
        width: 155,
        maxWidth: 155,
        cursor: "pointer",
      },
      render: (value: string) =>
        renderCell(TYPES.find((t) => t.value === value)?.label, null, null),
      renderHeader: (value: any) => renderHeader(value, `pair-type`),
      onClick: () => {
        onSort(`pair-type`);
      },
    },
    {
      key: "sk-actionability-score",
      title: "SK Actionability Score",
      dataIndex: "latestRelativeValuePairHistory.skActionabilityScore",
      style: {
        width: 155,
        maxWidth: 155,
        cursor: "pointer",
        textAlign: "center",
      },
      render: (value: number) => renderNumberCell(value, null, null, "0,0"),
      renderHeader: (value: any) =>
        renderHeader(value, `sk-actionability-score`),
      onClick: () => {
        onSort(`sk-actionability-score`);
      },
    },
    {
      key: "sk-liquidity-score",
      title: "SK Investability Score",
      dataIndex: "latestRelativeValuePairHistory.skLiquidityScore",
      style: {
        width: 155,
        maxWidth: 155,
        cursor: "pointer",
        textAlign: "center",
      },
      render: (value: number) => renderNumberCell(value, null, null, "0,0"),
      renderHeader: (value: any) => renderHeader(value, `sk-liquidity-score`),
      onClick: () => {
        onSort(`sk-liquidity-score`);
      },
    },
    {
      key: "z-score",
      title: "xSD 1 Yr Mean",
      dataIndex: "latestRelativeValuePairHistory.zScore",
      style: {
        width: 155,
        maxWidth: 155,
        cursor: "pointer",
        textAlign: "center",
      },
      render: (value: number) => renderZScoreCell(value, null, null, "0,0.00"),
      renderHeader: (value: any) => renderHeader(value, `z-score`),
      onClick: () => {
        onSort(`z-score`);
      },
    },
  ];

  const paywall = useMemo(() => {
    if (!authenticated || !account?.canViewExecutivesAnalytics) {
      return (
        <div className="flex items-center justify-center mt-[-30px] mb-[30px]">
          <Button
            onClick={() => {
              if (!authenticated) {
                toggleModal("sign-up");
              } else if (!account?.canViewExecutivesAnalytics) {
                toggleStripeModal();
              }
            }}
            className="!bg-smart-teal !ml-0 !mr-0 w-[200px]"
          >
            Load More
          </Button>
        </div>
      );
    }
  }, [
    account?.canViewExecutivesAnalytics,
    authenticated,
    toggleModal,
    toggleStripeModal,
  ]);

  return (
    <div className="mt-[15px]">
      <div className="mt-[30px] px-[5px] md:px-[20px] pr-[5px] flex flex-col md:flex-row space-y-[15px] md:space-y-0">
        <div className="flex-1">
          <div className="max-w-[630px]">
            <h1 className="mb-0 font-bold text-2xl">Relative Value</h1>
            <p className="text-sm">
              Monitor price ratios of closely correlated pairs such as HoldCos,
              and Share classes
            </p>
            <Descriptions />
          </div>
        </div>
        <div className="">
          {authenticated && account?.canViewExecutivesAnalytics && (
            <RequestSubscriptionWidget activityKey="relative_value.request_trial" />
          )}
        </div>
      </div>

      <div className="mt-[30px]">
        <div>
          {mobile ? (
            (modalRelativeValue || pairId) && (
              <Modal
                customBackButtonLabel="&nbsp;"
                onClose={() => {
                  router.back();
                  setModalRelativeValue(undefined);
                }}
              >
                <ToolRelativeValueModal
                  pairId={pairId}
                  pairRecord={modalRelativeValue}
                />
              </Modal>
            )
          ) : (
            <div
              className={twMerge(
                modalRelativeValue || pairId
                  ? "h-[calc(min(100vh-60px,700px))] md:h-[calc(100vh-60px)] md:w-[950px]"
                  : "w-0 h-0",
                "fixed left-0 md:left-auto right-0 md:top-[60px] bottom-0 md:bottom-auto",
                "bg-white",
                "shadow-[0_4px_30px_rgba(0,0,0,0.25)] rounded-l-[16px]",
                "duration-200 ease-out transform transition-height md:transition-width",
                "py-[15px] pb-[15px]",
                "z-10",
                "overflow-hidden"
              )}
            >
              {(modalRelativeValue || pairId) && (
                <>
                  <ToolRelativeValueModal
                    pairId={modalRelativeValue?.pairId || pairId}
                    pairRecord={modalRelativeValue}
                    onClose={() => {
                      const query = { ...router.query };
                      delete query.pair;
                      router.replace(
                        { pathname: router.pathname, query },
                        undefined,
                        {
                          shallow: true,
                        }
                      );
                      setModalRelativeValue(undefined);
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
        <Filter
          onChange={(filters: any) => {
            setFilters(filters);
          }}
        />
        <ToolTable
          swr={{ data, mutate, isValidating, size, setSize }}
          tableColumns={tableColumns}
          onRowClick={onRowClick}
          rowClassNameCallback={() => {
            return "cursor-pointer group";
          }}
          cellClassNameCallback={(record: any) =>
            twMerge(
              record.pairId === (modalRelativeValue?.pairId || pairId)
                ? "!bg-nearly-white"
                : "",
              "group-hover:!bg-nearly-white",
              "h-[36px]"
            )
          }
          dataCallback={(data) => uniqBy(data, (i: any) => i.pairId)}
          allowLoadMore={authenticated && account?.canViewExecutivesAnalytics}
        />
        {paywall}
        {showStripeModal && <AccountUpgradeModal onClose={toggleStripeModal} />}
      </div>
    </div>
  );
}
